import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays, CalendarX2, CheckCircle2, Clock, Loader2, RefreshCw, ThumbsUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pub = axios.create();
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const pretty = (t) => { const [h, m] = t.split(":").map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };
const longDate = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }); };
const jsDow = (d) => (d.getDay() + 6) % 7;
const STATUS_TXT = { requested: "Requested – awaiting confirmation", scheduled: "Scheduled", confirmed: "Confirmed", completed: "Completed", cancelled: "Cancelled" };

export default function ManageAppointment() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const autoConfirm = params.get("confirm") === "1";
  const confirmedOnce = useRef(false);
  const [appt, setAppt] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("view"); // view | reschedule | cancel
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState(null);
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let alive = true;
    pub.get(`${API}/public/appointment/${token}`).then((r) => { if (alive) setAppt((cur) => cur || r.data); }).catch(() => alive && setErr("This appointment link is not valid or has expired."));
    return () => { alive = false; };
  }, [token]);
  useEffect(() => {
    if (!autoConfirm || !appt || confirmedOnce.current) return;
    confirmedOnce.current = true;
    if (appt.can_confirm) act("confirm");
    else if (appt.status === "confirmed") setNotice("You're all set – this appointment is already confirmed.");
  }, [appt, autoConfirm]); // eslint-disable-line
  useEffect(() => {
    if (!date || mode !== "reschedule") return;
    setSlots(null); setTime("");
    pub.get(`${API}/public/appointment/${token}/slots`, { params: { date: iso(date) } }).then((r) => setSlots(r.data)).catch(() => setSlots({ open: false, slots: [], reason: "Could not load times" }));
  }, [date, mode, token]);

  const { minDay, maxDay } = useMemo(() => {
    const t = new Date(); t.setHours(0, 0, 0, 0);
    const max = new Date(t); max.setDate(max.getDate() + (appt?.horizon_days || 30));
    return { minDay: t, maxDay: max };
  }, [appt]);
  const closed = (d) => d < minDay || d > maxDay || (appt ? !appt.type.days.includes(jsDow(d)) : false);

  const act = async (path, body) => {
    setBusy(true); setErr("");
    try { const { data } = await pub.post(`${API}/public/appointment/${token}/${path}`, body || {}); setAppt(data); setMode("view"); setNotice(path === "cancel" ? "Your appointment has been cancelled." : path === "confirm" ? "Thanks – you're confirmed. See you then!" : "Your new time has been requested – we'll confirm shortly."); }
    catch (ex) { const d = ex.response?.data?.detail; setErr(typeof d === "string" ? d : "Something went wrong – please try again."); }
    finally { setBusy(false); }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-slate-100 py-6 px-3 sm:px-6" data-testid="manage-appointment-page">
      <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
        <div className="bg-blue-900 text-white px-5 py-4 flex items-center gap-3">
          {appt?.logo_b64 ? <img src={`data:image/jpeg;base64,${appt.logo_b64}`} alt="" className="h-10 w-auto rounded bg-white p-0.5" /> : <CalendarDays className="w-8 h-8" />}
          <div><p className="font-semibold leading-tight">{appt?.company_name || "Your appointment"}</p><p className="text-xs text-blue-100">Manage your appointment</p></div>
        </div>
        <div className="p-5 sm:p-7">{children}</div>
      </div>
      {appt?.company_phone && <p className="text-center text-xs text-slate-500 mt-4">Questions? Call {appt.company_phone}</p>}
    </div>
  );
  if (err && !appt) return shell(<p className="text-center text-slate-700 py-8" data-testid="manage-invalid">{err}</p>);
  if (!appt) return shell(<div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-700" /></div>);

  return shell(
    <>
      <p className="text-sm text-slate-500">Hello {appt.contact_name?.split(" ")[0] || "there"},</p>
      <h1 className="text-xl font-bold text-slate-900 mt-1">{appt.type.label}</h1>
      <div className="mt-3 rounded-xl border border-slate-200 p-4" data-testid="manage-summary">
        <p className="font-semibold text-blue-900 text-lg" data-testid="manage-when">{longDate(appt.date)} at {pretty(appt.time)}</p>
        <p className="text-sm text-slate-600 flex items-center gap-1 mt-1"><Clock className="w-3.5 h-3.5" /> about {appt.duration} minutes{appt.technician_name ? ` · with ${appt.technician_name}` : ""}</p>
        <p className={`text-xs mt-2 font-medium ${appt.status === "cancelled" ? "text-red-600" : "text-emerald-700"}`} data-testid="manage-status">{STATUS_TXT[appt.status] || appt.status}</p>
      </div>
      {notice && <p className="mt-3 text-sm text-emerald-700 flex items-center gap-1" data-testid="manage-notice"><CheckCircle2 className="w-4 h-4" /> {notice}</p>}
      {err && <p className="mt-3 text-sm text-red-600" data-testid="manage-error">{err}</p>}

      {appt.can_confirm && mode === "view" && (
        <Button className="w-full mt-5 h-12 text-base bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => act("confirm")} data-testid="manage-confirm-button">
          {busy ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <ThumbsUp className="w-5 h-5 mr-2" />}{busy ? "Confirming…" : "Yes, I'll be there – confirm"}
        </Button>
      )}
      {appt.can_change && mode === "view" && (
        <div className={`flex flex-col sm:flex-row gap-2 ${appt.can_confirm ? "mt-3" : "mt-5"}`}>
          <Button className="bg-blue-900 hover:bg-blue-800 flex-1" onClick={() => { setMode("reschedule"); setDate(null); }} data-testid="manage-reschedule-button"><RefreshCw className="w-4 h-4 mr-2" />Pick a new time</Button>
          <Button variant="outline" className="text-red-700 border-red-200 hover:bg-red-50 flex-1" onClick={() => setMode("cancel")} data-testid="manage-cancel-button"><CalendarX2 className="w-4 h-4 mr-2" />Cancel appointment</Button>
        </div>
      )}
      {mode === "cancel" && (
        <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4" data-testid="manage-cancel-confirm">
          <p className="text-sm text-red-800">Cancel this appointment? Our office will be notified. You can always book again later.</p>
          <div className="flex gap-2 mt-3"><Button variant="outline" onClick={() => setMode("view")} data-testid="manage-cancel-no">Keep it</Button><Button className="bg-red-600 hover:bg-red-700" disabled={busy} onClick={() => act("cancel")} data-testid="manage-cancel-yes">{busy ? "Cancelling…" : "Yes, cancel"}</Button></div>
        </div>
      )}
      {mode === "reschedule" && (
        <div className="mt-5" data-testid="manage-reschedule">
          <div className="grid sm:grid-cols-[auto,1fr] gap-5">
            <div className="rounded-xl border border-slate-200 p-1 justify-self-center"><Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} disabled={closed} fromDate={minDay} toDate={maxDay} defaultMonth={new Date(appt.date + "T00:00:00") > minDay ? new Date(appt.date + "T00:00:00") : minDay} /></div>
            <div>
              {!date && <p className="text-sm text-slate-500">Choose a day to see open times.</p>}
              {date && <p className="text-sm font-medium text-slate-800">{longDate(iso(date))}</p>}
              {date && !slots && <Loader2 className="w-5 h-5 animate-spin text-blue-700 mt-3" />}
              {date && slots && (!slots.open || slots.slots.length === 0) && <p className="text-sm text-amber-700 mt-3" data-testid="manage-no-slots">{slots.reason || "No openings that day."}</p>}
              {date && slots?.open && slots.slots.length > 0 && <div className="grid grid-cols-3 gap-2 mt-3" data-testid="manage-slots">{slots.slots.map((s) => <button key={s} type="button" onClick={() => setTime(s)} className={`rounded-lg border px-2 py-2 text-sm ${time === s ? "bg-blue-900 text-white border-blue-900" : "border-slate-200 hover:border-blue-700"}`} data-testid={`manage-slot-${s}`}>{pretty(s)}</button>)}</div>}
              <div className="flex gap-2 mt-4"><Button variant="outline" onClick={() => setMode("view")} data-testid="manage-reschedule-back">Back</Button><Button className="bg-blue-900 hover:bg-blue-800 flex-1" disabled={!time || busy} onClick={() => act("reschedule", { date: iso(date), time })} data-testid="manage-reschedule-submit">{busy ? "Saving…" : "Request this time"}</Button></div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
